/* eslint-disable react/no-did-update-set-state */

/**
 * Gallery.jsx
 * Created by Ali Alemi on 8/11/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { throttle } from 'lodash';
import * as Icons from './icons/Icons';
import { sluggify } from '../../helpers/stringHelper';
import { animatedScrollTo } from '../../helpers/scrollToHelper';

import announce from '../../helpers/announce';

export default class Gallery extends React.PureComponent {
    static propTypes = {
        /** The images and related information */
        images: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string,
            altText: PropTypes.string,
            caption: PropTypes.string
        })).isRequired,

        /** Shows the thumbnail strip at the bottom of the open gallery */
        hasThumbnailStrip: PropTypes.bool,

        /** The index of the image to be shown by default */
        activeIndex: PropTypes.number,

        /** Call back fired when the user clicks a thumbnail image. The image
         * index is returned as a parameter */
        handleClick: PropTypes.func
    };

    static defaultProps = {
        hasThumbnailStrip: true,
        activeIndex: 0,
        handleClick: undefined
    };

    constructor(props) {
        super(props);

        this.state = {
            activeIndex: props.activeIndex
        };

        this.throttledHandleResize = throttle(this.handleResize, 400);

        this.parent = React.createRef();
        this.buttonRefs = [];
    }

    componentDidMount() {
        const { activeIndex } = this.state;

        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('resize', this.throttledHandleResize);
        this.updateTrackScrollPosition();

        if (this.buttonRefs[activeIndex]) {
            setTimeout(() => {
                this.buttonRefs[activeIndex].focus();
            }, 0);
        }
    }

    componentDidUpdate(prevProps) {
        const { activeIndex } = this.props;

        if (activeIndex !== prevProps.activeIndex) {
            this.setState({ activeIndex });
        }

        this.updateTrackScrollPosition();
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('resize', this.throttledHandleResize);
    }

    handleResize = () => {
        this.updateTrackScrollPosition();
    }

    isMobile() {
        return window.innerWidth < 767;
    }

    updateTrackScrollPosition() {
        if (!this.props.hasThumbnailStrip || this.isMobile() || !this.parent.current) {
            return;
        }

        const parentWidth = this.parent.current.offsetWidth;
        const scrollPosition = this.parent.current.scrollLeft;
        const buttonWidth = this.buttonRefs[0].offsetWidth;
        const padding = this.buttonRefs[0].offsetLeft;
        const buttonPosition = this.buttonRefs[this.state.activeIndex].offsetLeft;

        if (buttonPosition + buttonWidth > parentWidth + scrollPosition) {
            const newPosition = buttonPosition + buttonWidth + padding - parentWidth;
            animatedScrollTo({
                element: this.parent.current,
                to: newPosition,
                duration: 250,
                scrollDirection: "scrollLeft"
            });
        }
        if (buttonPosition - scrollPosition < 0) {
            const newPosition = buttonPosition - padding;
            animatedScrollTo({
                element: this.parent.current,
                to: newPosition,
                duration: 250,
                scrollDirection: "scrollLeft"
            });
        }
    }

    navigateNext = (shouldNotMoveFocus = true) => {
        let nextIndex = this.state.activeIndex + 1;
        if (nextIndex >= this.props.images.length) {
            nextIndex = 0;
        }
        this.setState({
            activeIndex: nextIndex
        });

        const caption = this.props.images[nextIndex].caption;

        // Set the focus to the thumbnail button
        if (!shouldNotMoveFocus) {
            setTimeout(() => {
                this.buttonRefs[nextIndex].focus();
                announce(caption);
            }, 0);
        }
        else {
            announce(`${caption} (Image ${nextIndex + 1} of ${this.props.images.length})`);
        }
    }

    navigatePrev = (shouldNotMoveFocus = true) => {
        let prevIndex = this.state.activeIndex - 1;
        if (prevIndex < 0) {
            prevIndex = this.props.images.length - 1;
        }
        this.setState({ activeIndex: prevIndex });
        const caption = this.props.images[prevIndex].caption;

        // Set the focus to the thumbnail button
        if (!shouldNotMoveFocus) {
            setTimeout(() => {
                this.buttonRefs[prevIndex].focus();
                announce(caption);
            }, 0);
        }
        else {
            announce(`${caption} (Image ${prevIndex + 1} of ${this.props.images.length})`);
        }
    }

    handleOptionClick = (activeIndex) => (event) => {
        event.preventDefault();

        const { handleClick } = this.props;

        this.setState({
            activeIndex
        });

        if (handleClick) {
            handleClick(activeIndex);
        }
    }

    handleKeyDown = (event) => {
        if (event.defaultPrevented) {
            return;
        }

        const key = event.key || event.keyCode;

        if (key === 'ArrowRight' || key === '39' || key === 'ArrowDown' || key === '40') {
            event.preventDefault();
            this.navigateNext(false);
        }

        if (key === 'ArrowLeft' || key === '37' || key === 'ArrowUp' || key === '38') {
            event.preventDefault();
            this.navigatePrev(false);
        }
    }

    renderThumbnailImages() {
        const { images } = this.props;

        return images.map((image, index) => {
            return (
                <button
                    ref={(node) => {
                        this.buttonRefs[index] = node;
                    }}
                    className={cx('rec-gallery-thumbnail', {
                        active: index === this.state.activeIndex
                    })}
                    key={`${sluggify(image.url)}`}
                    onClick={this.handleOptionClick(index)}
                    aria-label={`Image ${index + 1} of ${images.length}: ${image.altText}`}>
                    <span>
                        <img
                            src={image.url}
                            alt={image.altText}
                            data-index={index} />
                    </span>
                </button>
            )
        });
    }

    render() {
        const { images, hasThumbnailStrip } = this.props;
        const currentImage = images[this.state.activeIndex];

        if (!images || images.length === 0) {
            return null;
        }

        // Some older images only have altText, we'll use that as a backup if there's no caption
        const caption = currentImage.caption || currentImage.altText;

        // Set the width for the thumbnail bar
        const widthOfThumb = 150;
        const thumbMargin = 16;
        const fullWidth = images.length * widthOfThumb;
        const fullWidthMargin = images.length * thumbMargin;
        const widthOfGalleryTrack = fullWidth + fullWidthMargin;

        const thumbsWidth = {
            width: widthOfGalleryTrack
        };

        return (
            <div className="rec-gallery">
                <div className="rec-gallery-main-wrap">
                    <div className="rec-gallery-slide">
                        <img
                            className="rec-gallery-current-image"
                            aria-label={`${caption}, Image ${this.state.activeIndex + 1} of ${images.length}`}
                            src={currentImage.url}
                            alt={currentImage.altText}
                            ref={this.currentImageRef} />
                        <button
                            type="button"
                            onClick={this.navigatePrev}
                            className="rec-gallery-arrow rec-gallery-prev"
                            aria-label="Previous Image">
                            <Icons.IconChevronLeft alt='Previous Image' />
                        </button>

                        <button
                            type="button"
                            onClick={this.navigateNext}
                            className="rec-gallery-arrow rec-gallery-next"
                            aria-label="Next Image">
                            <Icons.IconChevronRight alt='Next Image' />
                        </button>
                    </div>
                </div>

                <div id="rec-media-gallery-caption" className="rec-gallery-caption-group" aria-hidden="true">
                    <div>
                        <p>
                            {caption}
                        </p>
                        {currentImage.credit && (
                            <p className="rec-gallery-credit">
                                {currentImage.credit}
                            </p>
                        )}
                    </div>

                    <div className="rec-current-image-index">
                        {this.state.activeIndex + 1} of {images.length}
                    </div>
                </div>

                <div ref={this.parent} className={cx('rec-gallery-track', { 'hide': !hasThumbnailStrip })}>
                    <div ref={this.trackList} id="rec-gallery-track-list" className="rec-gallery-track-list" style={thumbsWidth}>
                        {this.renderThumbnailImages()}
                    </div>
                </div>
            </div>
        );
    }
}